import React, { useState, useEffect, useRef } from "react";
import { useQueryClient } from '@tanstack/react-query';
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Sidebar from "../startupdashboard/admin_sidebar";
import Header from "../startupdashboard/Adminheader";
import { uploadImage, getAllSectors, getAllStages, getCallToAction } from "../utils/api";
import ModernSidebar from "./admin_sidebar";
import ModernHeader from "./modern-header";
import { PhoneCall, Phone } from "lucide-react";
import { FiDollarSign, FiPlusCircle, FiMeh } from "react-icons/fi";
import {
  IndianRupee,
  TrendingUp,
  Building2,
  MapPin,
  Target,
  Briefcase,
  Eye,
  Trash2,
  Upload,
  X,
  Check,
  AlertTriangle,
  Rocket,
  DollarSign,
  Percent,
  PieChart,
  Award,
  Play,
  RefreshCw,
} from "lucide-react";
import { ClipLoader } from "react-spinners";
import { toast } from "react-hot-toast";
import { initializePayment } from "../utils/paymentUtils";
import { PaymentType } from "../utils/paymentTypes";
import { isGuest } from "../utils/isGuest";
import { useNavigate } from "react-router-dom";

const districts = [
  "Ariyalur", "Chengalpattu", "Chennai", "Coimbatore", "Cuddalore", "Dharmapuri", "Dindigul",
  "Erode", "Kallakurichi", "Kancheepuram", "Karur", "Krishnagiri", "Madurai", "Mayiladuthurai",
  "Nagapattinam", "Namakkal", "Nilgiris", "Perambalur", "Pudukkottai", "Ramanathapuram", "Ranipet",
  "Salem", "Sivaganga", "Tenkasi", "Thanjavur", "Theni", "Thoothukudi", "Tiruchirappalli",
  "Tirunelveli", "Tirupathur", "Tiruppur", "Tiruvallur", "Tiruvannamalai", "Tiruvarur", "Vellore",
  "Viluppuram", "Virudhunagar"
];

const initialFormData = {
  youtube: "",
  location: "",
  sector: "",
  shortDescription: "",
  longDescription: "",
  logoUrl: "",
  stage: "",
  status: "waiting",
  amountSeeking: "",
  equityOffered: "",
  valuation: "",
  fundUsage: "",
  minimumInvestment: "",
  ticketSize: "",
  roleProvided: "",
  amountRaised: 0,
};

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  formData,
  fundingFee,
  loading
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg w-full max-w-md p-6">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold text-gray-900">Confirm Funding Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 font-medium">Amount Seeking</p>
              <p className="text-lg font-semibold">₹{formData.amountSeeking}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Equity Offered</p>
              <p className="text-lg font-semibold">{formData.equityOffered}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Valuation</p>
              <p className="text-lg font-semibold">₹{formData.valuation}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Min Investment</p>
              <p className="text-lg font-semibold">₹{formData.minimumInvestment}</p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500 font-medium">Registration Fee</p>
              <p className="text-lg font-semibold">₹{fundingFee}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 bg-black text-white font-semibold rounded-lg shadow hover:shadow-md transition-all flex items-center gap-2"
          >
            {loading ? (
              <>
                <ClipLoader size={16} color="#fff" />
                Processing...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Pay ₹{fundingFee} & Submit
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const Funding = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [fundings, setFundings] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fundingToDelete, setFundingToDelete] = useState(null);
  const fileInputRef = useRef(null);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [startupProfile, setStartupProfile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [stages, setStages] = useState([]);
  const [viewingFunding, setViewingFunding] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [fundingFee, setFundingFee] = useState(0);
  const [loadingFee, setLoadingFee] = useState(false);
  const [showExpertModal, setShowExpertModal] = useState(false);
  const [sectorContacts, setSectorContacts] = useState({});
  const [caNumber, setCaNumber] = useState(null);
  const [fundingNumber, setFundingNumber] = useState(null);
  const [loadingNumber, setLoadingNumber] = useState(false);

  const userId = localStorage.getItem("startupId");
  const apiUrl = process.env.REACT_APP_API_URL;
  const queryClient = useQueryClient();
  const isGuestValid = isGuest()
  const navigate = useNavigate()

  // Call to action
  useEffect(() => {
    if (showExpertModal) {
      setLoadingNumber(true);
      getCallToAction()
        .then((res) => {
          let number = null;
          if (Array.isArray(res)) {
            const fundingData = res.find((item) =>
              item.role?.toLowerCase().includes("funding")
            );
            if (fundingData) number = fundingData.number;
          } else if (res?.role?.toLowerCase().includes("funding")) {
            number = res.number;
          }
          setFundingNumber(number);
        })
        .catch((err) => {
          console.error("Error fetching funding number:", err);
        })
        .finally(() => {
          setLoadingNumber(false);
        });
    }
  }, [showExpertModal]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchFundings = async () => {
      try {
        setIsLoading(true);
        const res = await axios.get(`${apiUrl}/api/fundings/me`, { params: { userId } });
        setFundings(res.data);
      } catch (error) {
        console.error("Error fetching fundings", error.response || error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFundings();
  }, [apiUrl, userId]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [sectorsRes, stagesRes] = await Promise.all([
          getAllSectors(),
          getAllStages()
        ]);

        const stagesData = Array.isArray(stagesRes) ? stagesRes : stagesRes?.data || [];
        const sectorsData = Array.isArray(sectorsRes) ? sectorsRes : sectorsRes?.data || [];

        setSectors(sectorsData);
        setStages(stagesData);

      } catch (error) {
        console.error("Error fetching dropdown data", error);
        setSectors([]);
        setStages([]);
      }
    };

    fetchDropdownData();
  }, []);

  useEffect(() => {
    const fetchFundingFee = async () => {
      try {
        setLoadingFee(true);
        const res = await axios.get(`${apiUrl}/api/static-options`);
        // grab the first option’s value
        const fee = res.data.options?.[0]?.value;
        if (typeof fee === 'number') {
          setFundingFee(fee);
        } else {
          throw new Error('Funding fee not found in response');
        }
      } catch (error) {
        console.error("Error fetching funding fee", error);
        toast.error("Failed to load funding fee");
        setFundingFee(1000); // Fallback amount
      } finally {
        setLoadingFee(false);
      }
    };
    fetchFundingFee();
  }, [apiUrl]);

  const getSectorContacts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sector-contacts`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sector contacts:', error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchSectorContacts = async () => {
      try {
        const contacts = await getSectorContacts();
        setSectorContacts(contacts);
      } catch (error) {
        console.error('Error fetching sector contacts:', error);
      }
    };

    fetchSectorContacts();
  }, []);

  const resetForm = () => {
    setShowForm(false);
    setEditId(null);
    setFormData({
      ...initialFormData,
      logoUrl: startupProfile?.logoUrl || ""
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const toggleForm = () => {
    if (showForm) {
      resetForm();
    } else {
      setShowForm(true);
      setEditId(null);
      setFormData({
        ...initialFormData,
        logoUrl: startupProfile?.logoUrl || ""
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;

    if (['amountSeeking', 'equityOffered', 'valuation', 'minimumInvestment'].includes(name)) {
      if (value && !/^[0-9.]*$/.test(value)) return;
    }

    if (['fundUsage', 'roleProvided'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) return;
    }

    if (name === "logoUrl" && files && files[0]) {
      const file = files[0];
      try {
        const uploadRes = await uploadImage(file);
        if (uploadRes.success) {
          setFormData(prev => ({ ...prev, logoUrl: uploadRes.url }));
          if (fileInputRef.current) fileInputRef.current.value = "";
        }
      } catch (err) {
        console.error("Logo upload failed", err);
        alert("Failed to upload logo. Please try again.");
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCallExpert = (phoneNumber) => {
    const cleanedPhoneNumber = phoneNumber.replace(/[^\d+]/g, '');

    window.location.href = `tel:${cleanedPhoneNumber}`;
  };

  const getSectorPhoneNumber = () => {
    if (!startupProfile || !startupProfile.sector || !sectorContacts || Object.keys(sectorContacts).length === 0) return null;

    const startupSector = startupProfile.sector.toLowerCase().trim();

    const sectorKey = Object.keys(sectorContacts).find(
      key => key.toLowerCase().includes(startupSector) || startupSector.includes(key.toLowerCase())
    );

    if (sectorKey && sectorContacts[sectorKey] && sectorContacts[sectorKey].length > 0) {
      return sectorContacts[sectorKey][0];
    }

    if (sectorContacts.Funding && sectorContacts.Funding.length > 0) {
      return sectorContacts.Funding[0];
    }

    return null;
  };

  const getAnyContactNumber = () => {
    if (!sectorContacts || Object.keys(sectorContacts).length === 0) return null;

    if (sectorContacts.Funding && sectorContacts.Funding.length > 0) {
      return sectorContacts.Funding[0];
    }

    const firstSector = Object.keys(sectorContacts)[0];
    if (sectorContacts[firstSector] && sectorContacts[firstSector].length > 0) {
      return sectorContacts[firstSector][0];
    }

    return null;
  };

  const hasContacts = () => {
    return sectorContacts && Object.keys(sectorContacts).length > 0 &&
      Object.values(sectorContacts).some(numbers => numbers && numbers.length > 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowConfirmation(true);
  };

  const handlePaymentAndSubmit = async () => {
    setPaymentProcessing(true);

    try {
      // Store the form data temporarily
      sessionStorage.setItem('funding_data', JSON.stringify({
        ...formData,
        paymentAmount: fundingFee
      }));

      // Initialize payment
      const paymentResult = await initializePayment({
        type: PaymentType.FUNDING,
        amount: fundingFee,
        token: localStorage.getItem("Startuptoken"),
        returnUrl: `${window.location.origin}/payment-callback`
      });

    } catch (error) {
      console.error('Payment Error:', error);
      toast.error(error.message || 'Payment processing failed');
      setPaymentProcessing(false);
    }
  };

  const handlePaymentSuccess = async (orderId) => {
    try {
      const actualUserId = localStorage.getItem("startupId");
      if (!actualUserId) throw new Error("No startupId found - please log in again");

      const payload = { userId: actualUserId, ...formData };

      if (editId) {
        await axios.put(`${apiUrl}/api/fundings/${editId}`, payload);
      } else {
        await axios.post(`${apiUrl}/api/fundings`, payload);
      }

      // Refresh the list
      const res = await axios.get(`${apiUrl}/api/fundings/me`, {
        params: { userId: actualUserId }
      });
      setFundings(res.data);

      resetForm();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);

      // Invalidate dashboard stats so funding counts update on the dashboard immediately
      try {
        queryClient.invalidateQueries(['dashboardStats']);
      } catch (err) {
        console.warn('Failed to invalidate dashboardStats query', err);
      }

    } catch (err) {
      console.error("Submission failed", err.response || err.message);
      toast.error("Failed to submit funding after payment");
    } finally {
      setPaymentProcessing(false);
      setShowConfirmation(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${apiUrl}/api/fundings/${fundingToDelete}`);
      setFundings((prev) => prev.filter((f) => f._id !== fundingToDelete));
      setShowDeleteConfirm(false);
      // Invalidate dashboard stats after deletion
      try {
        queryClient.invalidateQueries(['dashboardStats']);
      } catch (err) {
        console.warn('Failed to invalidate dashboardStats query', err);
      }
    } catch (err) {
      console.error("Delete failed", err);
      alert("Delete operation failed.");
    }
  };

  const handleView = (id) => {
    const funding = fundings.find((f) => f._id === id);
    setViewingFunding(funding);
    setShowModal(true);
  };

  const fetchStartupProfile = async () => {
    try {
      const startupId = localStorage.getItem("startupId");
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/startups/${startupId}`);
      setStartupProfile(response.data);

      setFormData(prev => ({
        ...prev,
        logoUrl: response.data.logoUrl || ""
      }));
    } catch (error) {
      console.error("Error fetching startup profile:", error);
    }
  };

  useEffect(() => {
    fetchStartupProfile();
  }, []);

  useEffect(() => {
    if (showModal && !editingId) {
      fetchStartupProfile();
    }
  }, [showModal, editingId]);

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'waiting':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStageColor = (stage) => {
    switch (stage.toLowerCase()) {
      case 'ideation':
        return 'from-blue-500 to-indigo-500';
      case 'pre-seed':
        return 'from-purple-500 to-violet-500';
      case 'seed':
        return 'from-green-500 to-teal-500';
      case 'series a':
        return 'from-yellow-500 to-amber-500';
      case 'series b':
        return 'from-orange-500 to-red-500';
      case 'series c':
        return 'from-pink-500 to-rose-500';
      default:
        return 'from-gray-500 to-slate-500';
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <ModernSidebar
        isOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
        fullName={fullName}
        email={email}
      />

      <div className={`flex flex-col flex-1 transition-all duration-300 ${isMobile ? "ml-0" : "ml-16 md:ml-20 lg:ml-20"}`}>
        <ModernHeader toggleSidebar={toggleSidebar} isMobile={isMobile} />

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="space-y-6">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-black via-gray-900 to-slate-600 bg-clip-text text-transparent">
                  Funding Dashboard
                </h1>
                <p className="text-gray-600 text-sm sm:text-base mt-1 sm:mt-2">Manage your funding rounds and investor relations</p>
              </div>
              <div className="flex gap-3">
                {/* Add the Call to Expert button here */}
                <button
                  onClick={() => setShowExpertModal(true)}
                  className="group relative overflow-hidden bg-black hover:bg-gray-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 sm:gap-3 text-sm sm:text-base"
                >
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                  <PhoneCall className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Call to Expert</span>
                </button>

                <button
                  onClick={() => {
                    if (isGuestValid) {
                      toast.error("Please login to create funding!")
                      navigate('/login')
                    } else {
                      toggleForm()
                    }
                  }}
                  className="group relative overflow-hidden bg-black hover:from-blue-700 hover:to-purple-700 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 sm:gap-3 text-sm sm:text-base"
                >
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                  {showForm ? (
                    <>
                      <X className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span>Close Form</span>
                    </>
                  ) : (
                    <>
                      <FiPlusCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span>Create Funding</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium">Total Fundings</p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{fundings.length}</p>
                  </div>
                  <div className="bg-blue-100 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                    <Rocket className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium">Waiting for Approval</p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                      {fundings.filter((f) => f.status === "waiting").length}
                    </p>
                  </div>
                  <div className="bg-yellow-100 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                    <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-yellow-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium">Approved</p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                      {fundings.filter((f) => f.status === "approved").length}
                    </p>
                  </div>
                  <div className="bg-green-100 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-600" />
                  </div>
                </div>
              </div>
              <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-xs sm:text-sm font-medium">Total Raised</p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                      ₹{fundings.reduce((sum, f) => sum + (f.amountRaised || 0), 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-emerald-100 p-2 sm:p-3 rounded-lg sm:rounded-xl">
                    <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-emerald-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Success Message */}
            <AnimatePresence>
              {showSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-lg flex items-center gap-2 sm:gap-3 text-sm sm:text-base"
                >
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                  <span className="font-semibold">Funding submission successful!</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form Section */}
            <AnimatePresence>
              {showForm && (
                <motion.div
                  initial={{ opacity: 0, y: -30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -30 }}
                  className="bg-white/80 backdrop-blur-sm border border-white/20 shadow-lg sm:shadow-2xl rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8"
                >
                  <div className="mb-4 sm:mb-6 md:mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
                      {editId ? "Edit Funding Round" : "Create New Funding Round"}
                    </h2>
                    <p className="text-gray-600 text-sm sm:text-base">
                      {editId
                        ? "Update your funding details"
                        : "Fill in the details to create a new funding opportunity"}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 md:space-y-8">
                    {/* Basic Information */}
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                        <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">YouTube Pitch Video</label>
                          <div className="relative">
                            <Play className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                            <input
                              type="text"
                              name="youtube"
                              value={formData.youtube}
                              onChange={handleChange}
                              placeholder="https://youtube.com/watch?v=..."
                              className="w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-xs sm:text-sm"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Location</label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                            <select
                              name="location"
                              value={formData.location}
                              onChange={handleChange}
                              className="w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-xs sm:text-sm"
                              required
                            >
                              <option value="">Select District</option>
                              {districts.map((d) => (
                                <option key={d} value={d}>
                                  {d}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Sector</label>
                          <div className="relative">
                            <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                            <select
                              name="sector"
                              value={formData.sector}
                              onChange={handleChange}
                              className="w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-xs sm:text-sm"
                              required
                            >
                              <option value="">Select Sector</option>
                              {sectors.map((sector) => (
                                <option key={sector._id} value={sector.sectorName}>
                                  {sector.sectorName}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Funding Stage</label>
                          <div className="relative">
                            <Target className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                            <select
                              name="stage"
                              value={formData.stage}
                              onChange={handleChange}
                              className="w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-xs sm:text-sm"
                              required
                            >
                              <option value="">Select Stage</option>
                              {stages.map((stage) => (
                                <option key={stage._id} value={stage.stageName}>
                                  {stage.stageName}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Company Logo */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Company Logo</label>
                      {formData.logoUrl ? (
                        <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                          <img
                            src={formData.logoUrl}
                            alt="Company logo"
                            className="w-12 h-12 sm:w-16 sm:h-16 object-contain border rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, logoUrl: "" }))}
                            className="text-red-600 hover:text-red-800 text-xs sm:text-sm font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                      <input
                        type="file"
                        name="logoUrl"
                        accept="image/*"
                        onChange={handleChange}
                        ref={fileInputRef}
                        className="w-full border-2 border-gray-200 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200 text-xs sm:text-sm"
                      />
                    </div>

                    {/* Descriptions */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Company Description</h3>
                      <div className="space-y-3 sm:space-y-4 md:space-y-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Short Description</label>
                          <input
                            type="text"
                            name="shortDescription"
                            value={formData.shortDescription}
                            onChange={handleChange}
                            placeholder="Brief overview of your company (1-2 sentences)"
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-xs sm:text-sm"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Detailed Description</label>
                          <textarea
                            name="longDescription"
                            value={formData.longDescription}
                            onChange={handleChange}
                            placeholder="Detailed description of your company, product, market opportunity, and business model"
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-xs sm:text-sm"
                            rows={3}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Financial Details */}
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                        Financial Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Amount Seeking</label>
                          <div className="relative">
                            <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                            <input
                              type="text"
                              name="amountSeeking"
                              value={formData.amountSeeking}
                              onChange={handleChange}
                              placeholder="e.g., 50 Lakhs, 2 Crores"
                              className="w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-xs sm:text-sm"
                              inputMode="numeric"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Equity Offered (%)</label>
                          <div className="relative">
                            <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                            <input
                              type="text"
                              name="equityOffered"
                              value={formData.equityOffered}
                              onChange={handleChange}
                              placeholder="e.g., 10%, 15%"
                              className="w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-xs sm:text-sm"
                              inputMode="numeric"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Company Valuation</label>
                          <div className="relative">
                            <PieChart className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                            <input
                              type="text"
                              name="valuation"
                              value={formData.valuation}
                              onChange={handleChange}
                              placeholder="e.g., 5 Crores, 10 Crores"
                              className="w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-xs sm:text-sm"
                              inputMode="numeric"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Minimum Investment</label>
                          <div className="relative">
                            <Target className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                            <input
                              type="text"
                              name="minimumInvestment"
                              value={formData.minimumInvestment}
                              onChange={handleChange}
                              placeholder="e.g., 5 Lakhs, 10 Lakhs"
                              className="w-full pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-xs sm:text-sm"
                              inputMode="numeric"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Fund Usage</label>
                          <input
                            type="text"
                            name="fundUsage"
                            value={formData.fundUsage}
                            onChange={handleChange}
                            placeholder="How will you use the funds?"
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-xs sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                        <Award className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                        Additional Information
                      </h3>
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                          Role Provided to Investor
                        </label>
                        <input
                          type="text"
                          name="roleProvided"
                          value={formData.roleProvided}
                          onChange={handleChange}
                          placeholder="e.g., Advisory Role, Board Member"
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-xs sm:text-sm"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg sm:rounded-xl hover:bg-gray-50 transition-all duration-200 text-xs sm:text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 sm:px-6 py-2 sm:py-3 bg-black hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 text-xs sm:text-sm"
                      >
                        <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                        {editId ? "Update Funding" : "Submit Funding"}
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Funding Cards */}
            {fundings && fundings.length > 0 ? (
              <div className="grid gap-4 sm:gap-6 md:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {fundings.map((funding) => (
                  <motion.div
                    key={funding._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="group relative bg-white/80 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-[1.02]"
                  >
                    {/* Status Badge */}
                    <div
                      className={`absolute top-2 sm:top-4 right-2 sm:right-4 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs font-bold border ${getStatusColor(funding.status)}`}
                    >
                      {funding.status.toUpperCase()}
                    </div>

                    {/* Company Logo */}
                    <div className="flex justify-center mb-4 sm:mb-6">
                      <div className="relative">
                        <img
                          src={funding.logoUrl || "/no_image.jpg"}
                          alt="Company Logo"
                          className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-xl sm:rounded-2xl shadow-lg border-2 sm:border-4 border-white"
                        />
                        <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 w-4 h-4 sm:w-6 sm:h-6 bg-green-500 rounded-full border-2 border-white"></div>
                      </div>
                    </div>

                    {/* Company Info */}
                    <div className="text-center mb-4 sm:mb-6">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">{funding.sector}</h3>
                      <div
                        className={`inline-block px-3 py-1 sm:px-4 sm:py-2 rounded-full text-white text-xs sm:text-sm font-semibold bg-gradient-to-r ${getStageColor(funding.stage)} shadow-md sm:shadow-lg`}
                      >
                        {funding.stage}
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-center mb-4 sm:mb-6 line-clamp-3 text-xs sm:text-sm">{funding.shortDescription}</p>

                    {/* Details Grid */}
                    <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="bg-blue-100 p-1.5 sm:p-2 rounded-md sm:rounded-lg">
                          <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xs sm:text-xs text-gray-500 font-medium">Location</p>
                          <p className="text-xs sm:text-sm font-semibold text-gray-900">{funding.location}</p>
                        </div>
                      </div>

                      {funding.amountSeeking && (
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="bg-green-100 p-1.5 sm:p-2 rounded-md sm:rounded-lg">
                            <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="text-2xs sm:text-xs text-gray-500 font-medium">Seeking</p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-900">₹{funding.amountSeeking}</p>
                          </div>
                        </div>
                      )}

                      {funding.equityOffered && (
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="bg-purple-100 p-1.5 sm:p-2 rounded-md sm:rounded-lg">
                            <PieChart className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-2xs sm:text-xs text-gray-500 font-medium">Equity</p>
                            <p className="text-xs sm:text-sm font-semibold text-gray-900">{funding.equityOffered}%</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 sm:gap-3">
                      {funding.youtube && (
                        <a
                          href={funding.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-black hover:from-red-600 hover:to-pink-600 text-white font-semibold py-2 px-3 sm:py-2.5 sm:px-4 rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm"
                        >
                          <Play className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">Watch</span>
                        </a>
                      )}
                      <button
                        onClick={() => handleView(funding._id)}
                        className="flex-1 bg-black hover:from-blue-600 hover:to-indigo-600 text-white font-semibold py-2 px-3 sm:py-2.5 sm:px-4 rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm"
                      >
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">View</span>
                      </button>
                      <button
                        onClick={() => {
                          setFundingToDelete(funding._id)
                          setShowDeleteConfirm(true)
                        }}
                        className="flex-1 bg-black hover:from-red-600 hover:to-pink-600 text-white font-semibold py-2 px-3 sm:py-2.5 sm:px-4 rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm"
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Delete</span>
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <>
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20 bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/20 shadow-lg">
                    <ClipLoader
                      size={40}
                      color={"#6366f1"}
                      loading={isLoading}
                    />
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mt-4 sm:mt-6 mb-2 sm:mb-3">Loading Funding Rounds...</h3>
                    <p className="text-gray-600 text-center max-w-xs sm:max-w-md text-sm sm:text-base">
                      Please wait while we fetch your funding information.
                    </p>
                  </div>
                ) : (
                  fundings.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20 bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/20 shadow-lg">
                      <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-4 sm:p-6 rounded-full mb-4 sm:mb-6 flex items-center justify-center">
                        <Rocket className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-blue-600" />
                      </div>
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 sm:mb-3">No Funding Rounds Yet</h3>
                      <p className="text-gray-600 text-center max-w-xs sm:max-w-md mb-4 sm:mb-6 text-sm sm:text-base">
                        Start your fundraising journey by creating your first funding round. Connect with investors and grow
                        your startup.
                      </p>
                      <button

                        onClick={() => {
                          if (isGuestValid) {
                            toast.error("Please login to create funding!")
                            navigate('/login')
                          } else {
                            setShowForm(true)
                          }
                        }
                        }
                      className="bg-black hover:from-blue-700 hover:to-purple-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2 text-xs sm:text-sm"
                      >
                      <FiPlusCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                      Create Your First Funding
                    </button>
                    </div>
            )
                )}
          </>
            )}

          {/* View Funding Modal */}
          {showModal && viewingFunding && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl sm:rounded-2xl shadow-xl w-full max-w-md sm:max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="p-4 sm:p-6 md:p-8">
                  <div className="flex justify-between items-start mb-4 sm:mb-6">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Funding Round Details</h2>
                      <div className={`mt-1 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs font-bold border ${getStatusColor(viewingFunding.status)}`}>
                        {viewingFunding.status.toUpperCase()}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                    {/* Left Column */}
                    <div>
                      <div className="mb-4 sm:mb-6">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
                          <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                          Basic Information
                        </h3>
                        <div className="space-y-2 sm:space-y-3">
                          <div>
                            <p className="text-xs sm:text-sm text-gray-500 font-medium">Sector</p>
                            <p className="text-sm sm:text-base font-semibold text-gray-900">{viewingFunding.sector}</p>
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm text-gray-500 font-medium">Stage</p>
                            <p className="text-sm sm:text-base font-semibold text-gray-900">{viewingFunding.stage}</p>
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm text-gray-500 font-medium">Location</p>
                            <p className="text-sm sm:text-base font-semibold text-gray-900">{viewingFunding.location}</p>
                          </div>
                          {viewingFunding.youtube && (
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500 font-medium">Pitch Video</p>
                              <a
                                href={viewingFunding.youtube}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                              >
                                <Play className="w-3 h-3 sm:w-4 sm:h-4" />
                                Watch Pitch Video
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mb-4 sm:mb-6">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
                          <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                          Financial Details
                        </h3>
                        <div className="space-y-2 sm:space-y-3">
                          {viewingFunding.amountSeeking && (
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500 font-medium">Amount Seeking</p>
                              <p className="text-sm sm:text-base font-semibold text-gray-900">₹{viewingFunding.amountSeeking}</p>
                            </div>
                          )}
                          {viewingFunding.equityOffered && (
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500 font-medium">Equity Offered</p>
                              <p className="text-sm sm:text-base font-semibold text-gray-900">{viewingFunding.equityOffered}%</p>
                            </div>
                          )}
                          {viewingFunding.valuation && (
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500 font-medium">Company Valuation</p>
                              <p className="text-sm sm:text-base font-semibold text-gray-900">₹{viewingFunding.valuation}</p>
                            </div>
                          )}
                          {viewingFunding.minimumInvestment && (
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500 font-medium">Minimum Investment</p>
                              <p className="text-sm sm:text-base font-semibold text-gray-900">₹{viewingFunding.minimumInvestment}</p>
                            </div>
                          )}
                          {viewingFunding.fundUsage && (
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500 font-medium">Fund Usage</p>
                              <p className="text-sm sm:text-base font-semibold text-gray-900">{viewingFunding.fundUsage}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div>
                      <div className="mb-4 sm:mb-6">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Company Description</h3>
                        <div className="space-y-2 sm:space-y-3">
                          <div>
                            <p className="text-xs sm:text-sm text-gray-500 font-medium">Short Description</p>
                            <p className="text-sm sm:text-base font-semibold text-gray-900">{viewingFunding.shortDescription}</p>
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm text-gray-500 font-medium">Detailed Description</p>
                            <p className="text-sm sm:text-base text-gray-700 whitespace-pre-line">{viewingFunding.longDescription}</p>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4 sm:mb-6">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
                          <Award className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                          Additional Information
                        </h3>
                        <div className="space-y-2 sm:space-y-3">
                          {viewingFunding.roleProvided && (
                            <div>
                              <p className="text-xs sm:text-sm text-gray-500 font-medium">Role for Investor</p>
                              <p className="text-sm sm:text-base font-semibold text-gray-900">{viewingFunding.roleProvided}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-center">
                        {viewingFunding.logoUrl && (
                          <img
                            src={viewingFunding.logoUrl}
                            alt="Company Logo"
                            className="w-24 h-24 sm:w-32 sm:h-32 object-contain border rounded-lg"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl sm:rounded-2xl shadow-lg w-full max-w-sm p-4 sm:p-6 md:p-8"
              >
                <div className="text-center mb-4 sm:mb-6">
                  <div className="bg-red-100 p-3 sm:p-4 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-red-600" />
                  </div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Delete Funding Round</h2>
                  <p className="text-gray-600 text-xs sm:text-sm">
                    Are you sure you want to delete this funding round? This action cannot be undone.
                  </p>
                </div>

                <div className="flex gap-2 sm:gap-3 md:gap-4">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg sm:rounded-xl hover:bg-gray-50 transition-all duration-200 text-xs sm:text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 text-xs sm:text-sm"
                  >
                    Delete
                  </button>
                </div>
              </motion.div>
            </div>
          )}

          {/* Expert Modal */}
          {showExpertModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[9999] p-4">
              <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative transform animate-in zoom-in-95 duration-300 expert-modal">
                <div className="absolute top-4 right-4">
                  <button
                    onClick={() => setShowExpertModal(false)}
                    className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
                  >
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                </div>

                <div className="text-center mb-6">
                  <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <PhoneCall className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Call Our Funding Expert</h2>
                  <p className="text-gray-600">
                    Get personalized assistance with your funding needs and investor relations.
                  </p>
                </div>

                {hasContacts() ? (
                  <>
                    {startupProfile && startupProfile.sector && caNumber ? (
                      <div className="bg-blue-50 rounded-xl p-5 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Sector-Specific Support</h3>
                        <p className="text-gray-700 mb-3">
                          Your startup is in the <span className="font-semibold">{startupProfile.sector}</span> sector.
                          Contact our dedicated funding expert for specialized assistance.
                        </p>
                        <div className="flex items-center justify-center gap-2 text-lg font-semibold text-blue-700">
                          <Phone className="w-5 h-5" />
                          <span>
                            {loadingNumber ? "loading..." : fundingNumber || getAnyContactNumber()}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-purple-50 rounded-xl p-5 mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">General Support</h3>
                        <p className="text-gray-700 mb-3">
                          Contact our funding experts for assistance with your fundraising needs.
                        </p>
                        <div className="flex items-center justify-center gap-2 text-lg font-semibold text-purple-700">
                          <Phone className="w-5 h-5" />
                          <span>
                            {loadingNumber ? "loading..." : fundingNumber || getAnyContactNumber()}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-5 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Support Unavailable</h3>
                    <p className="text-gray-700 mb-3">
                      Our support team contact information is currently being updated.
                    </p>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">
                        Please check back later or email us at support@tactos.in
                      </p>
                    </div>
                  </div>
                )}

                {hasContacts() && (
                  <button
                    onClick={() =>
                      handleCallExpert(
                        fundingNumber || getSectorPhoneNumber() || getAnyContactNumber()
                      )
                    }
                    disabled={loadingNumber}
                    className={`w-full font-semibold py-3 px-6 rounded-xl shadow-lg flex items-center justify-center gap-3 transition-all duration-200 
    ${loadingNumber
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white hover:shadow-xl transform hover:scale-105"
                      }`}
                  >
                    <PhoneCall className="w-5 h-5" />
                    {loadingNumber ? "Fetching number..." : "Call Now"}
                  </button>
                )}
              </div>
            </div>
          )}

          <ConfirmationModal
            isOpen={showConfirmation}
            onClose={() => setShowConfirmation(false)}
            onConfirm={handlePaymentAndSubmit}
            formData={formData}
            loading={paymentProcessing}
          />
      </div>
    </main>
      </div >
    </div >
  )
}

export default Funding